/**
 * 公开页 SEO 元数据应用工具。
 *
 * 管理端"SEO 元数据"配置此前零消费(getPublishedPage 返回了 seo 但无任何页面注入),
 * 本工具是唯一接线点:公开首页挂载时把已发布的 SEO 配置写入 document.title 与 meta 标签。
 */

interface SeoInput {
  pageTitle?: string
  pageDescription?: string
  keywords?: string[]
}

/** upsert 一个 meta 标签(name 或 property 定位),content 为 null 时移除该标签 */
function upsertMeta(attr: 'name' | 'property', key: string, content: string | null): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (content === null) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** 首次 applySeo 前的文档快照,供离开公开首页时恢复(否则 SEO 值泄漏到后续所有 SPA 页面) */
let snapshot: {
  title: string
  description: string | null
  keywords: string | null
} | null = null

/**
 * 将 SEO 配置应用到当前文档。
 * 首次应用时快照原始 title/meta;各字段为空时保留现有值不动
 * (避免发布不完整配置把标题清空)。
 */
export function applySeo(seo: SeoInput | null | undefined): void {
  if (!seo) return
  if (!snapshot) {
    const desc = document.head.querySelector<HTMLMetaElement>('meta[name="description"]')
    const kw = document.head.querySelector<HTMLMetaElement>('meta[name="keywords"]')
    snapshot = {
      title: document.title,
      description: desc?.getAttribute('content') ?? null,
      keywords: kw?.getAttribute('content') ?? null,
    }
  }
  if (seo.pageTitle) {
    document.title = seo.pageTitle
  }
  if (seo.pageDescription) {
    upsertMeta('name', 'description', seo.pageDescription)
  }
  if (seo.keywords && seo.keywords.length > 0) {
    upsertMeta('name', 'keywords', seo.keywords.join(', '))
  }
}

/** 恢复到首次 applySeo 之前的 title/meta(路由离开公开首页时调用) */
export function restoreSeo(): void {
  if (!snapshot) return
  document.title = snapshot.title
  upsertMeta('name', 'description', snapshot.description)
  upsertMeta('name', 'keywords', snapshot.keywords)
  snapshot = null
}
